import React from "react";
import { useState } from "react";
import { useRouter } from "next/router";
import { Hooks } from "@xt/xtbase";
const { useIntl, FormattedMessage } = Hooks;
import useIsDesktop from "@/hooks/useIsDesktop";

import axios from "utils/axios";
import FormItem from "components/App/FormItem";
import sessionStorage from "utils/sessionStorage";
import ButtonBig from "components/Antd/Button/big";
import FormItemPassword from "components/App/FormItem/Password";

import { doRsaEncrypt, getAbsoluteUrl, regexp } from "utils/method";
import { post_passwdPublicKey } from "api/uaapi/uaa";
import { put_nResetPasswd } from "api/uaapi/user";
import { message } from "antd";

export default function StepThree() {
  const intl = useIntl();
  const router = useRouter();
  const { isDesktop, isReady } = useIsDesktop();
  const isSpikexPc = isReady && isDesktop;

  React.useEffect(() => {
    const faResultId = sessionStorage.get("faResultId");
    if (!faResultId) {
      const query = { ...router.query };
      delete query.step;
      if (typeof location !== "undefined") {
        location.replace(
          getAbsoluteUrl({
            pathname: "/login",
            locale: router.locale,
            query,
          })
        );
      } else {
        router.replace({
          pathname: "/login",
          query,
        });
      }
    }

    return () => {
      sessionStorage.remove("faResultId");
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const faResultId = sessionStorage.keyList["faResultId"]
    ? sessionStorage.get("faResultId")
    : "";

  const [newPwdStat, setNewPwdStat] = useState({
    isOK: false,
    errMsg: "",
  });

  const [confirmPwdStat, setConfirmPwdStat] = useState({
    isOK: true,
    errMsg: "",
  });

  const verifyNewPwd = (bool) => {
    const errMsg = intl.formatMessage({ id: "resetPwd.newPwdTip" });
    let status = { isOK: true, errMsg: "" };
    if (!newPwd) {
      status = { isOK: false, errMsg: bool ? errMsg : "" };
    } else if (!regexp("loginPassword", newPwd)) {
      status = {
        isOK: false,
        errMsg: intl.formatMessage({ id: "app.pwdErrorTip" }, { 0: 8 }),
      };
    }
    setNewPwdStat(status);
    return status;
  };

  React.useEffect(() => {
    setNewPwdStat({ isOK: false, errMsg: "" });
  }, [newPwd]);

  React.useEffect(() => {
    if (!confirmPwd) {
      setConfirmPwdStat({ isOK: true, errMsg: "" });
      return;
    }
    setConfirmPwdStat({
      isOK: newPwd === confirmPwd,
      errMsg:
        newPwd !== confirmPwd
          ? intl.formatMessage({ id: "resetPwd.pwdConfirmTip" })
          : "",
    });
  }, [newPwd, confirmPwd, intl]);

  const handleConfirmPwdInput = (val) => {
    setConfirmPwd(val);
  };

  const handleConfirm = async () => {
    const pwdStatus = verifyNewPwd(true);
    if (!pwdStatus.isOK) return;

    if (!confirmPwd) {
      setConfirmPwdStat({
        isOK: false,
        errMsg: intl.formatMessage({ id: "resetPwd.pwdConfirmSecTip" }),
      });
      return;
    }
    if (confirmPwd !== newPwd) {
      setConfirmPwdStat({
        isOK: false,
        errMsg: intl.formatMessage({ id: "resetPwd.pwdConfirmTip" }),
      });
      return;
    }

    setLoading(true);
    const passwdPublicRes = await axios.asyncAwait(post_passwdPublicKey, {
      errorPop: true,
    });
    if (passwdPublicRes.err) return setLoading(false);
    const { publicKey, passwdId } = passwdPublicRes.data;

    put_nResetPasswd({
      data: {
        faResultId: faResultId,
        newLoginPwd: doRsaEncrypt(publicKey, newPwd),
        passwdId: passwdId,
        regChannel: "xt",
      },
      isReturnResponse: true,
    })
      .then((res) => {
        const { code, msg = "" } = res.data;
        if (code != 200) {
          message.error(msg);
          return;
        }
        message.success(msg);
        const query = { ...router.query };
        delete query.step;
        if (typeof location !== "undefined")
          location.href = getAbsoluteUrl({
            pathname: "/login",
            locale: router.locale,
            query,
          });
        else
          router.push({
            pathname: "/login",
            query,
          });
        sessionStorage.remove("faResultId");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={"stepForm"}>
      <FormItem
        title={<FormattedMessage id="resetPwd.newPwd" />}
        errMsg={newPwdStat.errMsg}
        hint={
          isSpikexPc && !newPwdStat.errMsg
            ? intl.formatMessage({ id: "auth.passwordHint" })
            : undefined
        }
      >
        <FormItemPassword
          autoFocus={true}
          value={newPwd}
          onInput={setNewPwd}
          onBlur={() => verifyNewPwd(true)}
          isErr={!!newPwdStat.errMsg}
          popover={!isSpikexPc}
          placeholder={
            isSpikexPc
              ? intl.formatMessage({ id: "auth.passwordPlaceholderRegister" })
              : undefined
          }
        />
      </FormItem>

      <FormItem
        title={<FormattedMessage id="resetPwd.pwdConfirm" />}
        errMsg={confirmPwdStat.errMsg}
      >
        <FormItemPassword
          value={confirmPwd}
          popover={false}
          onInput={handleConfirmPwdInput}
          isErr={!!confirmPwdStat.errMsg}
          placeholder={
            isSpikexPc
              ? intl.formatMessage({ id: "auth.passwordPlaceholderRegister" })
              : undefined
          }
        />
      </FormItem>

      <ButtonBig
        tabIndex={3}
        className={"bigBtn"}
        onClick={handleConfirm}
        loading={loading}
      >
        <FormattedMessage id="app.confirm" />
      </ButtonBig>
    </div>
  );
};
